import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CompareProvider } from "@/context/CompareContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import SearchDashboard from "@/pages/SearchDashboard";
import ProgramDetail from "@/pages/ProgramDetail";
import Compare from "@/pages/Compare";
import ReporterDashboard from "@/pages/ReporterDashboard";
import ProviderProposals from "@/pages/ProviderProposals";
import AdminDashboard from "@/pages/AdminDashboard";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return <div className="grid min-h-screen place-items-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function ChangePasswordRoute() {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return <div className="grid min-h-screen place-items-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <ChangePassword />;
}

function Home() {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return <div className="grid min-h-screen place-items-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.role === "latp_admin" || user.role === "auditor") return <Navigate to="/admin" replace />;
  if (user.role === "provider_reporter" || user.role === "provider_admin")
    return <Navigate to="/report" replace />;
  return <Navigate to="/search" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CompareProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors offset={72} />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/change-password" element={<ChangePasswordRoute />} />
              <Route path="/" element={<Home />} />
              <Route path="/search" element={<Protected><SearchDashboard /></Protected>} />
              <Route path="/programs/:id" element={<Protected><ProgramDetail /></Protected>} />
              <Route path="/compare" element={<Protected><Compare /></Protected>} />
              <Route
                path="/report"
                element={
                  <Protected roles={["provider_reporter", "provider_admin", "latp_admin"]}>
                    <ReporterDashboard />
                  </Protected>
                }
              />
              <Route
                path="/proposals"
                element={
                  <Protected roles={["provider_admin", "latp_admin"]}>
                    <ProviderProposals />
                  </Protected>
                }
              />
              <Route
                path="/admin"
                element={
                  <Protected roles={["latp_admin", "auditor"]}>
                    <AdminDashboard />
                  </Protected>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </CompareProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
